import { Extension, Node as TiptapNode } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';

import WysiwygHeading from './headings';
import { normalizePastedHtml } from './paste';

function parseEmptyAnchor(element: HTMLElement) {
  return element.textContent?.trim() ? false : null;
}

function shouldRenderButtonTarget(href: string, target: unknown) {
  return Boolean(target) && /^(?:https?:|ftp:|mailto:|tel:)/i.test(href.trim());
}

const WysiwygEditorKeyboardShortcuts = Extension.create({
  name: 'wysiwygEditorKeyboardShortcuts',

  addKeyboardShortcuts() {
    return {
      'Mod-Alt-5': () => this.editor.commands.toggleBulletList(),
      'Mod-Alt-6': () => this.editor.commands.toggleOrderedList(),
      'Mod-Alt-q': () => this.editor.commands.toggleBlockquote(),
    };
  },
});

const WysiwygButton = TiptapNode.create({
  name: 'wysiwygButton',
  priority: 1000,
  group: 'block',
  content: 'inline*',
  defining: true,

  addAttributes() {
    return {
      href: {
        default: '',
      },
      class: {
        default: 'btn btn-content',
      },
      target: {
        default: null,
      },
      dataPage: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-page'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['dataPage']
            ? { 'data-page': String(attributes['dataPage']) }
            : {},
      },
      dataItem: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-item'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['dataItem']
            ? { 'data-item': String(attributes['dataItem']) }
            : {},
      },
      dataFolder: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute('data-folder'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['dataFolder']
            ? { 'data-folder': String(attributes['dataFolder']) }
            : {},
      },
      dataApp: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-app'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['dataApp']
            ? { 'data-app': String(attributes['dataApp']) }
            : {},
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'a.btn.btn-content' },
      { tag: 'a[class*="bx-btn"]' },
      { tag: 'button' },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const href = String(HTMLAttributes['href'] ?? '');
    const { target, ...buttonAttributes } = HTMLAttributes;

    return [
      'a',
      {
        ...buttonAttributes,
        ...(shouldRenderButtonTarget(href, target)
          ? { target: String(target) }
          : {}),
        class: buttonAttributes['class'] ?? 'btn btn-content',
        href,
      },
      0,
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      const contentDOM = document.createElement('span');
      const className = node.attrs['class'];

      contentDOM.className =
        typeof className === 'string' && className.trim()
          ? className
          : 'btn btn-content';

      dom.append(contentDOM);

      return {
        dom,
        contentDOM,
      };
    };
  },
});

const WysiwygLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      dataPage: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-page'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['dataPage']
            ? { 'data-page': String(attributes['dataPage']) }
            : {},
      },
      dataItem: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-item'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['dataItem']
            ? { 'data-item': String(attributes['dataItem']) }
            : {},
      },
      dataFolder: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute('data-folder'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['dataFolder']
            ? { 'data-folder': String(attributes['dataFolder']) }
            : {},
      },
      dataApp: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-app'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['dataApp']
            ? { 'data-app': String(attributes['dataApp']) }
            : {},
      },
    };
  },
});

const WysiwygImage = TiptapNode.create({
  name: 'wysiwygImage',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[src]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', HTMLAttributes];
  },
});

const WysiwygAnchor = TiptapNode.create({
  name: 'wysiwygAnchor',
  group: 'inline',
  inline: true,
  atom: true,
  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
      },
      name: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'a[id]:not([href])', getAttrs: parseEmptyAnchor },
      { tag: 'a[name]:not([href])', getAttrs: parseEmptyAnchor },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      {
        ...HTMLAttributes,
      },
    ];
  },

  addNodeView() {
    return ({ editor, getPos, node }) => {
      let anchorName = '';
      const token = document.createElement('span');
      const icon = document.createElement('span');
      const label = document.createElement('span');

      token.className = 'wysiwyg-editor-shell__anchor-token';
      token.contentEditable = 'false';
      token.dataset['wysiwygEditorAnchorToken'] = '';

      icon.className = 'wysiwyg-editor-shell__anchor-token-icon';
      icon.setAttribute('aria-hidden', 'true');

      label.className = 'wysiwyg-editor-shell__anchor-token-label';

      token.append(icon, label);

      const syncAnchorName = (nextNode: typeof node) => {
        anchorName = String(
          nextNode.attrs['name'] ?? nextNode.attrs['id'] ?? ''
        );
        token.setAttribute('data-anchor-name', anchorName);
        label.textContent = anchorName;
      };

      syncAnchorName(node);

      token.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const position = getPos();

        editor.view.dom.dispatchEvent(
          new CustomEvent('wysiwyg-anchor-token-click', {
            bubbles: true,
            detail: {
              anchorName,
              position,
            },
          })
        );
      });

      return {
        dom: token,
        update(updatedNode) {
          if (updatedNode.type.name !== 'wysiwygAnchor') {
            return false;
          }

          syncAnchorName(updatedNode);

          return true;
        },
      };
    };
  },
});

const WysiwygTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
      },
    };
  },
});

export function getWysiwygEditorExtensions() {
  return [
    StarterKit.configure({
      heading: false,
    }),
    WysiwygHeading,
    WysiwygButton,
    WysiwygImage,
    WysiwygLink.configure({
      autolink: false,
      HTMLAttributes: {
        rel: null,
        target: null,
      },
      protocols: ['ted'],
      openOnClick: false,
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Underline,
    WysiwygAnchor,
    WysiwygTable.configure({
      resizable: false,
    }),
    TableRow,
    TableHeader,
    TableCell,
    WysiwygEditorKeyboardShortcuts,
  ];
}

interface WysiwygEditorPropsOptions {
  id: string;
  ariaDescribedby?: string;
  ariaLabelledby?: string;
}

export function getWysiwygEditorProps({
  id,
  ariaDescribedby,
  ariaLabelledby,
}: WysiwygEditorPropsOptions) {
  return {
    attributes: {
      id,
      class: 'wysiwyg-editor-shell__prosemirror',
      'aria-keyshortcuts': 'Alt+F10',
      ...(ariaDescribedby ? { 'aria-describedby': ariaDescribedby } : {}),
      ...(ariaLabelledby ? { 'aria-labelledby': ariaLabelledby } : {}),
      'data-test-ui-input-group-wysiwyg-editor-textfield': '',
    },
    transformPastedHTML: normalizePastedHtml,
  };
}
